import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

type SankeyData = {
  production_emissions_intensity: any;
  gathering_and_boosting_emissions_intensity: any;
  processing_emissions_intensity: any;
  transmission_emissions_intensity: any;
  liquefaction_emissions_intensity: any;
  shipping_emissions_intensity: any;
};

interface SankeyDiagramProps {
  data: SankeyData;
  basin?: any;
  destination?: any;
  allocatedMethod?: any;
  type?: any;
  tableData?: any;
}

const stageColors = [
  { name: 'Upstream', color: '#A8D5BA' },
  { name: 'Processing', color: '#F8B195' },
  { name: 'Transmission', color: '#C5DDE8' },
  { name: 'Liquefaction', color: '#F8E9A1' },
  { name: 'Shipping', color: '#D5A6BD' },
  { name: 'LNG', color: '#FBC4AB' },
  { name: 'Methane Emission', color: '#B0BEC5' },
  { name: 'Fuel Use', color: '#D9C2BA' },
  { name: 'Processing hydrocarbon liquids', color: '#A5B8D8' },
  { name: 'Upstream hydrocarbon liquids', color: '#B2DFDB' },
  { name: 'CO2', color: '#91cc75' },
  { name: 'CH4', color: '#5470c6' },
];

const getStageColor = (stageName: string) => {
  return stageColors.find((stage) => stage.name === stageName)?.color || '#000';
};

const EnergyFlowSankey: React.FC<SankeyDiagramProps> = ({ data, basin }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const UpstreamToME =
      (data.production_emissions_intensity
        .energy_loss_from_ch4_emission_at_production_d +
        data.gathering_and_boosting_emissions_intensity
          .energy_loss_from_ch4_emission_at_gathering_d) *
      1000;

    const ProcessingToME =
      data.processing_emissions_intensity
        .energy_loss_from_ch4_emission_at_processing_d * 1000;

    const TransmissionToME =
      data.transmission_emissions_intensity
        .energy_loss_from_ch4_emission_at_transmission_c * 1000;

    const LiquefactionToME =
      data.liquefaction_emissions_intensity
        .energy_loss_from_ch4_emission_at_liquefaction_c * 1000;

    const ShippingToME =
      data.shipping_emissions_intensity
        .energy_loss_from_ch4_emission_at_shipping_c * 1000;

    const UpstreamToFU =
      (data.production_emissions_intensity
        .energy_loss_from_gas_consumption_at_production_c +
        data.gathering_and_boosting_emissions_intensity
          .energy_loss_from_gas_consumption_at_gathering_c) *
      1000;

    const ProcessingToFU =
      data.processing_emissions_intensity
        .energy_loss_from_gas_consumption_at_processing_c * 1000;

    const TransmissionToFU =
      data.transmission_emissions_intensity
        .energy_loss_from_gas_consumption_at_transmission_b * 1000;

    const LiquefactionToFU =
      data.liquefaction_emissions_intensity
        .energy_loss_from_gas_consumption_at_liquefaction_b * 1000;

    const ShippingToFU =
      data.shipping_emissions_intensity
        .energy_loss_from_gas_consumption_at_shipping_b * 1000;

    const Upstream =
      data.production_emissions_intensity.energy_input_into_production_e * 1000;

    const UpstreamToProcessing = Upstream - (UpstreamToFU + UpstreamToME);

    const ProcessingToTransmission =
      UpstreamToProcessing - (ProcessingToME + ProcessingToFU);

    const TransmissionToLiquefaction =
      ProcessingToTransmission - (TransmissionToME + TransmissionToFU);

    const LiquefactionToShipping =
      TransmissionToLiquefaction - (LiquefactionToME + LiquefactionToFU);

    const ShippingToLNG =
      data.shipping_emissions_intensity.energy_output_from_shipping_a * 1000;

    const UpstreamHydrocarbon =
      (data.production_emissions_intensity
        .energy_loss_from_co_products_at_production_b +
        data.gathering_and_boosting_emissions_intensity
          .energy_loss_from_co_products_at_gathering_b) *
      1000;

    const ProcessingHydrocarbon =
      data.processing_emissions_intensity
        .energy_loss_from_co_products_at_processing_b * 1000;

    const nodes = [
      { name: 'Upstream', value: Upstream },
      { name: 'Processing', value: UpstreamToProcessing },
      { name: 'Transmission', value: ProcessingToTransmission },
      { name: 'Liquefaction', value: TransmissionToLiquefaction },
      { name: 'Shipping', value: LiquefactionToShipping },
      { name: 'LNG', value: ShippingToLNG },
      {
        name: 'Methane Emission',
        value:
          UpstreamToME +
          ProcessingToME +
          TransmissionToME +
          LiquefactionToME +
          ShippingToME,
      },
      {
        name: 'Fuel Use',
        value:
          UpstreamToFU +
          ProcessingToFU +
          TransmissionToFU +
          LiquefactionToFU +
          ShippingToFU,
      },
      {
        name: 'Processing hydrocarbon liquids',
        value: ProcessingHydrocarbon,
      },
      ...(basin === 'Permian'
        ? [
            {
              name: 'Upstream hydrocarbon liquids',
              value: UpstreamHydrocarbon,
            },
          ]
        : []),
    ];

    const sortedNodes = nodes.sort((a, b) => {
      const predefinedOrder = [
        'Upstream',
        'Processing',
        'Transmission',
        'Liquefaction',
        'Shipping',
        'LNG',
        'Methane Emission',
        'Fuel Use',
        'Processing hydrocarbon liquids',
        'Upstream hydrocarbon liquids',
      ];

      return predefinedOrder.indexOf(a.name) - predefinedOrder.indexOf(b.name);
    });

    const scaleLink = 1;

    const links = [
      {
        source: 'Upstream',
        target: 'Processing',
        value: UpstreamToProcessing,
      },
      {
        source: 'Processing',
        target: 'Transmission',
        value: ProcessingToTransmission,
      },
      {
        source: 'Transmission',
        target: 'Liquefaction',
        value: TransmissionToLiquefaction,
      },
      {
        source: 'Liquefaction',
        target: 'Shipping',
        value: LiquefactionToShipping,
      },
      {
        source: 'Shipping',
        target: 'LNG',
        value: ShippingToLNG,
      },
      {
        source: 'Upstream',
        target: 'Methane Emission',
        value: UpstreamToME * scaleLink,
      },
      {
        source: 'Processing',
        target: 'Methane Emission',
        value: ProcessingToME * scaleLink,
      },
      {
        source: 'Transmission',
        target: 'Methane Emission',
        value: TransmissionToME * scaleLink,
      },
      {
        source: 'Liquefaction',
        target: 'Methane Emission',
        value: LiquefactionToME * scaleLink,
      },
      {
        source: 'Shipping',
        target: 'Methane Emission',
        value: ShippingToME * scaleLink,
      },
      {
        source: 'Upstream',
        target: 'Fuel Use',
        value: UpstreamToFU * scaleLink,
      },
      {
        source: 'Processing',
        target: 'Fuel Use',
        value: ProcessingToFU * scaleLink,
      },
      {
        source: 'Transmission',
        target: 'Fuel Use',
        value: TransmissionToFU * scaleLink,
      },
      {
        source: 'Liquefaction',
        target: 'Fuel Use',
        value: LiquefactionToFU * scaleLink,
      },
      {
        source: 'Shipping',
        target: 'Fuel Use',
        value: ShippingToFU * scaleLink,
      },
      {
        source: 'Processing',
        target: 'Processing hydrocarbon liquids',
        value: ProcessingHydrocarbon,
      },
      ...(basin === 'Permian'
        ? [
            {
              source: 'Upstream',
              target: 'Upstream hydrocarbon liquids',
              value: UpstreamHydrocarbon,
            },
          ]
        : []),
    ];

    const option = {
      title: {
        text: 'Energy Flows Across Modeled Supply Chains',
        left: 'center',
        top: 0,
      },
      tooltip: {
        trigger: 'item',
        formatter: (info: {
          dataType: string;
          name: any;
          data: { source: any; target: any; value: any };
        }) => {
          if (info.dataType === 'node') {
            return `${info.name} ${info.data.value.toFixed(2)} kJ`;
          }
          if (info.dataType === 'edge') {
            return `${info.data.source} → ${info.data.target}: ${info.data.value.toFixed(2)} kJ`;
          }
          return '';
        },
      },
      series: [
        {
          type: 'sankey',
          left: 50,
          top: 40,
          right: 150,
          bottom: 50,
          layout: 'none',
          z: 2,
          coordinateSystem: 'view',
          orient: 'horizontal',
          nodeWidth: 100,
          nodeGap: 20,
          draggable: true,
          layoutIterations: 10,
          emphasis: {
            focus: 'adjacency',
          },
          data: sortedNodes.map((node) => ({
            ...node,
            itemStyle: {
              color: getStageColor(node.name),
            },
            label: {
              position:
                node.name === 'Methane Emission' ||
                node.name === 'Fuel Use' ||
                node.name === 'Processing hydrocarbon liquids'
                  ? 'right'
                  : node.name === 'Upstream hydrocarbon liquids'
                    ? 'bottom'
                    : 'inside',
              distance:
                node.name === 'Methane Emission' ||
                node.name === 'Fuel Use' ||
                node.name === 'Processing hydrocarbon liquids'
                  ? 10
                  : 20,
            },
          })),

          links: links.map((link) => ({
            ...link,
            lineStyle: {
              color: getStageColor(link.source),
            },
          })),
          nodeAlign: 'left',
          lineStyle: {
            color: 'source',
            curveness: 0.5,
          },
        },
      ],
    };

    chart.setOption(option);

    return () => {
      chart.dispose();
    };
  }, [data, basin]);

  return <div ref={chartRef} style={{ width: '100%', height: '700px' }} />;
};

const GHGIntensityBarChart: React.FC<SankeyDiagramProps> = ({
  data,
  basin,
  destination,
  tableData,
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const totalEmissions = [
      tableData[0].total + tableData[1].total,
      tableData[2].total,
      tableData[3].total,
      tableData[4].total,
      tableData[5].total,
    ];

    const option = {
      title: {
        text: 'GHG Intensity by Supply Chain Stage',
        left: 'center',
      },
      xAxis: {
        type: 'category',
        data: [`${basin} - ${destination}`],
      },
      yAxis: {
        type: 'value',
        name: 'GHG intensity gCO2e/MJ',
      },
      series: [
        {
          name: 'Upstream',
          type: 'bar',
          stack: 'GHG',
          data: [totalEmissions[0]],
          itemStyle: {
            color: getStageColor('Upstream'),
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (info: any) => {
              return `${info.value.toFixed(2)} gCO2e/MJ`;
            },
          },
        },
        {
          name: 'Processing',
          type: 'bar',
          stack: 'GHG',
          data: [totalEmissions[1]],
          itemStyle: {
            color: getStageColor('Processing'),
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (info: any) => {
              return `${info.value.toFixed(2)} gCO2e/MJ`;
            },
          },
        },
        {
          name: 'Transmission',
          type: 'bar',
          stack: 'GHG',
          data: [totalEmissions[2]],
          itemStyle: {
            color: getStageColor('Transmission'),
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (info: any) => {
              return `${info.value.toFixed(2)} gCO2e/MJ`;
            },
          },
        },
        {
          name: 'Liquefaction',
          type: 'bar',
          stack: 'GHG',
          data: [totalEmissions[3]],
          itemStyle: {
            color: getStageColor('Liquefaction'),
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (info: any) => {
              return `${info.value.toFixed(2)} gCO2e/MJ`;
            },
          },
        },
        {
          name: 'Shipping',
          type: 'bar',
          stack: 'GHG',
          data: [totalEmissions[4]],
          itemStyle: {
            color: getStageColor('Shipping'),
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (info: any) => {
              return `${info.value.toFixed(2)} gCO2e/MJ`;
            },
          },
        },
      ],
      legend: {
        data: [
          'Upstream',
          'Processing',
          'Transmission',
          'Liquefaction',
          'Shipping',
        ],
        bottom: 10,
      },
    };

    chart.setOption(option);

    return () => {
      chart.dispose();
    };
  }, [data]);

  return <div ref={chartRef} style={{ width: '100%', height: '400px' }} />;
};

const GHGIntensityPercentageChart: React.FC<SankeyDiagramProps> = ({
  data,
  basin,
  destination,
  tableData,
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const totalEmissions = [
      tableData[0].total + tableData[1].total,
      tableData[2].total,
      tableData[3].total,
      tableData[4].total,
      tableData[5].total,
    ];

    const totalSum = totalEmissions.reduce((a, b) => a + b, 0);

    const percentageData = totalEmissions.map(
      (value) => (value / totalSum) * 100,
    );

    const option = {
      title: {
        text: 'GHG Intensity by Supply Chain Stage as % Contribution',
        left: 'center',
      },
      xAxis: {
        type: 'category',
        data: [`${basin} - ${destination}`],
      },
      yAxis: {
        type: 'value',
        name: 'GHG intensity % Contribution',
        max: 100,
      },
      series: [
        {
          name: 'Upstream',
          type: 'bar',
          stack: 'GHG',
          data: [percentageData[0]],
          itemStyle: {
            color: getStageColor('Upstream'),
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (info: any) => {
              return `${info.value.toFixed(2)} %`;
            },
          },
        },
        {
          name: 'Processing',
          type: 'bar',
          stack: 'GHG',
          data: [percentageData[1]],
          itemStyle: {
            color: getStageColor('Processing'),
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (info: any) => {
              return `${info.value.toFixed(2)} %`;
            },
          },
        },
        {
          name: 'Transmission',
          type: 'bar',
          stack: 'GHG',
          data: [percentageData[2]],
          itemStyle: {
            color: getStageColor('Transmission'),
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (info: any) => {
              return `${info.value.toFixed(2)} %`;
            },
          },
        },
        {
          name: 'Liquefaction',
          type: 'bar',
          stack: 'GHG',
          data: [percentageData[3]],
          itemStyle: {
            color: getStageColor('Liquefaction'),
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (info: any) => {
              return `${info.value.toFixed(2)} %`;
            },
          },
        },
        {
          name: 'Shipping',
          type: 'bar',
          stack: 'GHG',
          data: [percentageData[4]],
          itemStyle: {
            color: getStageColor('Shipping'),
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (info: any) => {
              return `${info.value.toFixed(2)} %`;
            },
          },
        },
      ],
      legend: {
        data: [
          'Upstream',
          'Processing',
          'Transmission',
          'Liquefaction',
          'Shipping',
        ],
        bottom: 10,
      },
    };

    chart.setOption(option);

    return () => {
      chart.dispose();
    };
  }, [data]);

  return <div ref={chartRef} style={{ width: '100%', height: '400px' }} />;
};

const CO2AndMethaneIntensityChart: React.FC<SankeyDiagramProps> = ({
  data,
  allocatedMethod,
  type,
  tableData,
}) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const co2Data = [
      data.production_emissions_intensity.diesel_disposal_co2 +
        data.production_emissions_intensity.gas_combustion_co2 +
        data.production_emissions_intensity.gas_compression_co2 +
        data.production_emissions_intensity.gas_removal_emissions_co2 +
        data.production_emissions_intensity
          .flaring_emissions_of_flowback_gas_co2 +
        data.production_emissions_intensity.flaring_emissions_co2 +
        data.production_emissions_intensity.fugitives_emissions_co2 +
        data.production_emissions_intensity
          .venting_emissions_of_flowback_gas_co2 +
        data.production_emissions_intensity.venting_emissions_co2,
      data.gathering_and_boosting_emissions_intensity.gas_combustion_co2 +
        data.gathering_and_boosting_emissions_intensity.gas_compression_co2 +
        data.gathering_and_boosting_emissions_intensity
          .gas_removal_emissions_co2 +
        data.gathering_and_boosting_emissions_intensity.flaring_emissions_co2 +
        data.gathering_and_boosting_emissions_intensity
          .fugitives_emissions_co2 +
        data.gathering_and_boosting_emissions_intensity.venting_emissions_co2,

      data.processing_emissions_intensity.gas_combustion_co2 +
        data.processing_emissions_intensity.gas_compression_co2 +
        data.processing_emissions_intensity.gas_removal_emissions_co2 +
        data.processing_emissions_intensity.fugitives_emissions_co2 +
        data.processing_emissions_intensity.venting_emissions_co2,
      data.transmission_emissions_intensity
        .reciporcating_compression_intensity_co2 ??
        0 +
          data.transmission_emissions_intensity
            .centrifugal_compression_intensity_co2 ??
        0 + data.transmission_emissions_intensity.fugitives_emissions_co2 ??
        0 + data.transmission_emissions_intensity.venting_emissions_co2 ??
        0,
      data.liquefaction_emissions_intensity.gas_combustion_co2 +
        data.liquefaction_emissions_intensity.flaring_emissions_co2 +
        data.liquefaction_emissions_intensity.fugitives_emissions_co2 +
        data.liquefaction_emissions_intensity.venting_emissions_co2,
    ];

    const ch4Data = [
      data.production_emissions_intensity.gas_combustion_ch4 +
        data.production_emissions_intensity.gas_compression_ch4 +
        data.production_emissions_intensity.gas_removal_emissions_ch4 +
        data.production_emissions_intensity
          .flaring_emissions_of_flowback_gas_ch4 +
        data.production_emissions_intensity.flaring_emissions_ch4 +
        data.production_emissions_intensity.fugitives_emissions_ch4 +
        data.production_emissions_intensity
          .venting_emissions_of_flowback_gas_ch4 +
        data.production_emissions_intensity.venting_emissions_ch4 +
        data.production_emissions_intensity
          .top_down_measurement_informed_emissions_ch4,

      data.gathering_and_boosting_emissions_intensity.gas_combustion_ch4 +
        data.gathering_and_boosting_emissions_intensity.gas_compression_ch4 +
        data.gathering_and_boosting_emissions_intensity
          .gas_removal_emissions_ch4 +
        data.gathering_and_boosting_emissions_intensity.flaring_emissions_ch4 +
        data.gathering_and_boosting_emissions_intensity
          .fugitives_emissions_ch4 +
        data.gathering_and_boosting_emissions_intensity.venting_emissions_ch4 +
        data.gathering_and_boosting_emissions_intensity
          .top_down_measurement_informed_emissions_ch4,

      data.processing_emissions_intensity.gas_combustion_ch4 +
        data.processing_emissions_intensity.gas_compression_ch4 +
        data.processing_emissions_intensity.gas_removal_emissions_ch4 +
        data.processing_emissions_intensity.flaring_emissions_ch4 +
        data.processing_emissions_intensity.fugitives_emissions_ch4 +
        data.processing_emissions_intensity.venting_emissions_ch4 +
        data.processing_emissions_intensity
          .top_down_measurement_informed_emissions_ch4,
      data.transmission_emissions_intensity
        .reciporcating_compression_intensity_ch4 ??
        0 +
          data.transmission_emissions_intensity
            .centrifugal_compression_intensity_ch4 ??
        0 + data.transmission_emissions_intensity.fugitives_emissions_ch4 ??
        0 + data.transmission_emissions_intensity.venting_emissions_ch4 ??
        0 +
          data.transmission_emissions_intensity
            .top_down_measurement_informed_emissions_ch4 ??
        0,
      data.liquefaction_emissions_intensity.gas_combustion_ch4 +
        data.liquefaction_emissions_intensity.flaring_emissions_ch4 +
        data.liquefaction_emissions_intensity.fugitives_emissions_ch4 +
        data.liquefaction_emissions_intensity.venting_emissions_ch4 +
        data.liquefaction_emissions_intensity
          .top_down_measurement_informed_emissions_ch4,
    ];

    const co2DataAllocated = [
      data.production_emissions_intensity
        .allocation_method_based_diesel_disposal_co2 +
        data.production_emissions_intensity
          .allocation_method_based_gas_combustion_co2 +
        data.production_emissions_intensity
          .allocation_method_based_gas_compression_co2 +
        data.production_emissions_intensity
          .allocation_method_based_gas_removal_emissions_co2 +
        data.production_emissions_intensity
          .allocation_method_based_flaring_emissions_of_flowback_gas_co2 +
        data.production_emissions_intensity
          .allocation_method_based_flaring_emissions_co2 +
        data.production_emissions_intensity
          .allocation_method_based_fugitives_emissions_co2 +
        data.production_emissions_intensity
          .allocation_method_based_venting_emissions_of_flowback_gas_co2 +
        data.production_emissions_intensity
          .allocation_method_based_venting_emissions_co2,
      data.gathering_and_boosting_emissions_intensity
        .allocation_method_based_gas_combustion_co2 +
        data.gathering_and_boosting_emissions_intensity
          .allocation_method_based_gas_compression_co2 +
        data.gathering_and_boosting_emissions_intensity
          .allocation_method_based_gas_removal_emissions_co2 +
        data.gathering_and_boosting_emissions_intensity
          .allocation_method_based_flaring_emissions_co2 +
        data.gathering_and_boosting_emissions_intensity
          .allocation_method_based_fugitives_emissions_co2 +
        data.gathering_and_boosting_emissions_intensity
          .allocation_method_based_venting_emissions_co2,

      data.processing_emissions_intensity
        .allocation_method_based_gas_combustion_co2 +
        data.processing_emissions_intensity
          .allocation_method_based_gas_compression_co2 +
        data.processing_emissions_intensity
          .allocation_method_based_gas_removal_emissions_co2 +
        data.processing_emissions_intensity
          .allocation_method_based_fugitives_emissions_co2 +
        data.processing_emissions_intensity
          .allocation_method_based_venting_emissions_co2,
      data.transmission_emissions_intensity
        .allocation_method_based_reciporcating_compression_intensity_co2 ??
        0 +
          data.transmission_emissions_intensity
            .allocation_method_based_centrifugal_compression_intensity_co2 ??
        0 +
          data.transmission_emissions_intensity
            .allocation_method_based_fugitives_emissions_co2 ??
        0 +
          data.transmission_emissions_intensity
            .allocation_method_based_venting_emissions_co2 ??
        0,
      data.liquefaction_emissions_intensity
        .allocation_method_based_gas_combustion_co2 +
        data.liquefaction_emissions_intensity
          .allocation_method_based_flaring_emissions_co2 +
        data.liquefaction_emissions_intensity
          .allocation_method_based_fugitives_emissions_co2 +
        data.liquefaction_emissions_intensity
          .allocation_method_based_venting_emissions_co2,
    ];

    const ch4DataAllocated = [
      data.production_emissions_intensity
        .allocation_method_based_gas_combustion_ch4 +
        data.production_emissions_intensity
          .allocation_method_based_gas_compression_ch4 +
        data.production_emissions_intensity
          .allocation_method_based_gas_removal_emissions_ch4 +
        data.production_emissions_intensity
          .allocation_method_based_flaring_emissions_of_flowback_gas_ch4 +
        data.production_emissions_intensity
          .allocation_method_based_flaring_emissions_ch4 +
        data.production_emissions_intensity
          .allocation_method_based_fugitives_emissions_ch4 +
        data.production_emissions_intensity
          .allocation_method_based_venting_emissions_of_flowback_gas_ch4 +
        data.production_emissions_intensity
          .allocation_method_based_venting_emissions_ch4 +
        data.production_emissions_intensity
          .allocation_method_based_top_down_measurement_informed_emissions_ch4,

      data.gathering_and_boosting_emissions_intensity
        .allocation_method_based_gas_combustion_ch4 +
        data.gathering_and_boosting_emissions_intensity
          .allocation_method_based_gas_compression_ch4 +
        data.gathering_and_boosting_emissions_intensity
          .allocation_method_based_gas_removal_emissions_ch4 +
        data.gathering_and_boosting_emissions_intensity
          .allocation_method_based_flaring_emissions_ch4 +
        data.gathering_and_boosting_emissions_intensity
          .allocation_method_based_fugitives_emissions_ch4 +
        data.gathering_and_boosting_emissions_intensity
          .allocation_method_based_venting_emissions_ch4 +
        data.gathering_and_boosting_emissions_intensity
          .allocation_method_based_top_down_measurement_informed_emissions_ch4,

      data.processing_emissions_intensity
        .allocation_method_based_gas_combustion_ch4 +
        data.processing_emissions_intensity
          .allocation_method_based_gas_compression_ch4 +
        data.processing_emissions_intensity
          .allocation_method_based_gas_removal_emissions_ch4 +
        data.processing_emissions_intensity
          .allocation_method_based_flaring_emissions_ch4 +
        data.processing_emissions_intensity
          .allocation_method_based_fugitives_emissions_ch4 +
        data.processing_emissions_intensity
          .allocation_method_based_venting_emissions_ch4 +
        data.processing_emissions_intensity
          .allocation_method_based_top_down_measurement_informed_emissions_ch4,

      data.transmission_emissions_intensity
        .allocation_method_based_reciporcating_compression_intensity_ch4 ??
        0 +
          data.transmission_emissions_intensity
            .allocation_method_based_centrifugal_compression_intensity_ch4 ??
        0 +
          data.transmission_emissions_intensity
            .allocation_method_based_fugitives_emissions_ch4 ??
        0 +
          data.transmission_emissions_intensity
            .allocation_method_based_venting_emissions_ch4 ??
        0 +
          data.transmission_emissions_intensity
            .allocation_method_based_top_down_measurement_informed_emissions_ch4 ??
        0,

      data.liquefaction_emissions_intensity
        .allocation_method_based_gas_combustion_ch4 +
        data.liquefaction_emissions_intensity
          .allocation_method_based_flaring_emissions_ch4 +
        data.liquefaction_emissions_intensity
          .allocation_method_based_fugitives_emissions_ch4 +
        data.liquefaction_emissions_intensity
          .allocation_method_based_venting_emissions_ch4 +
        data.liquefaction_emissions_intensity
          .allocation_method_based_top_down_measurement_informed_emissions_ch4,
    ];

    const tableDataTotal =
      tableData &&
      tableData.filter((item: { stage: string }) => item.stage === 'Total')[0];

    const option =
      type === 'bar'
        ? {
            title: {
              text: 'CO2 and Methane Intensity by Stage',
              left: 'center',
            },
            legend: {
              top: 'bottom',
            },
            tooltip: {
              trigger: 'item',
              formatter: function (params: { value: any; name: any }) {
                const value = params.value;
                const formattedValue =
                  typeof value === 'number' ? value.toFixed(2) : value;
                return `${params.name}: ${formattedValue} gCO2e/MJ`;
              },
            },
            xAxis: {
              type: 'category',
              data: [
                'Production',
                'G&B',
                'Processing',
                'Transmission',
                'Liquefaction',
              ],
            },
            yAxis: {
              type: 'value',
              name: 'gCO2e/MJ',
            },
            series: [
              {
                name: 'CO2',
                type: 'bar',
                stack: 'GHG',
                itemStyle: {
                  color: getStageColor('CO2'),
                },
                data:
                  allocatedMethod === 'First Allocation Method'
                    ? co2Data
                    : co2DataAllocated,
              },
              {
                name: 'CH4',
                type: 'bar',
                stack: 'GHG',
                itemStyle: {
                  color: getStageColor('CH4'),
                },
                data:
                  allocatedMethod === 'First Allocation Method'
                    ? ch4Data
                    : ch4DataAllocated,
              },
            ],
          }
        : {
            title: {
              text: 'CO2 and Methane Intensity by Stage',
              left: 'center',
            },
            tooltip: {
              trigger: 'item',
              formatter: function (params: { value: any; name: any }) {
                const value = params.value;
                const formattedValue =
                  typeof value === 'number' ? value.toFixed(2) : value;
                return `${params.name}: ${formattedValue} %`;
              },
            },
            legend: {
              top: 'bottom',
            },
            series: [
              {
                type: 'pie',
                radius: '50%',
                data: [
                  {
                    value: (
                      (tableDataTotal.co2 * 100) /
                      tableDataTotal.total
                    ).toFixed(2),
                    name: 'CO2',
                    itemStyle: {
                      color: getStageColor('CO2'),
                    },
                  },
                  {
                    value: (
                      (tableDataTotal.ch4 * 100) /
                      tableDataTotal.total
                    ).toFixed(2),
                    name: 'CH4',
                    itemStyle: {
                      color: getStageColor('CH4'),
                    },
                  },
                ],
                emphasis: {
                  itemStyle: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                    shadowColor: 'rgba(0, 0, 0, 0.5)',
                  },
                },
              },
            ],
          };

    chart.setOption(option);

    return () => {
      chart.dispose();
    };
  }, [data]);

  return <div ref={chartRef} style={{ width: '100%', height: '400px' }} />;
};

export {
  EnergyFlowSankey,
  GHGIntensityBarChart,
  GHGIntensityPercentageChart,
  CO2AndMethaneIntensityChart,
};
